import { ContentAttachmentCreateInput } from '@vivport/backend/types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addContentAttachment,
  listContentAttachments,
  removeContentAttachment
} from '../api/content-attachment-api';
import { contentKeys } from './useContentQueries';

export const contentAttachmentKeys = {
  all: ['content-attachments'] as const,
  byParent: (contentId: number) => [...contentAttachmentKeys.all, contentId] as const
};

export const useContentAttachments = (contentId: number) =>
  useQuery({
    queryKey: contentAttachmentKeys.byParent(contentId),
    queryFn: () => listContentAttachments(contentId),
    enabled: contentId > 0
  });

export const useContentAttachmentsForContents = (contentIds: number[]) =>
  useQueries({
    queries: contentIds.map((contentId) => ({
      queryKey: contentAttachmentKeys.byParent(contentId),
      queryFn: () => listContentAttachments(contentId),
      enabled: contentId > 0
    })),
    combine: (results) => ({
      items: results.flatMap((result) => result.data?.items ?? []),
      isLoading: results.some((result) => result.isLoading)
    })
  });

export const useAddContentAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, payload }: { contentId: number; payload: ContentAttachmentCreateInput }) =>
      addContentAttachment(contentId, payload),
    onSuccess: (_, { contentId, payload }) => {
      // Filing MOVES: the server enforces one business parent, so writing this edge
      // deleted the child's previous one. The response does not name the old parent
      // and the client never knew it, so every parent's attachment list is stale —
      // invalidating the whole key space is what keeps the child rendered in ONE
      // place. Removal does not move anything, which is why its sibling below stays
      // narrow.
      queryClient.invalidateQueries({ queryKey: contentAttachmentKeys.all });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(payload.childContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
    }
  });
};

export const useRemoveContentAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, childContentId }: { contentId: number; childContentId: number }) =>
      removeContentAttachment(contentId, childContentId),
    onSuccess: (_, { contentId, childContentId }) => {
      queryClient.invalidateQueries({ queryKey: contentAttachmentKeys.byParent(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(childContentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
    }
  });
};
